<template>
  <div class="availability-calendar">
    <div v-if="!datesInconnues" class="calendar-container">
      <RadioGroup
        v-if="allowPeriods && !readonly"
        :model-value="mode"
        :options="modeOptions"
        class="mode-selector"
        @update:model-value="setMode"
      />

      <template v-if="showPeriods">
        <MonthPeriodPicker
          :model-value="periods"
          :readonly="readonly"
          @update:model-value="emit('update:periods', $event)"
        />
        <p v-if="!readonly" class="mode-hint">
          Cochez les mois envisagés : le devis affichera par exemple «&nbsp;Janvier / Février 2027&nbsp;».
        </p>
      </template>

      <template v-else>
      <!-- La grille reste visible en lecture : elle situe les dates retenues
           dans leur mois d'un coup d'œil ; seuls les clics de sélection sont
           désactivés (la navigation entre mois reste possible). -->
      <div class="calendar-wrapper date-grid-wrapper" :class="{ 'calendar-month-labels': monthNavLabels }">
        <div class="calendar-header date-grid-header">
          <Button
            type="button"
            variant="secondary"
            icon="fas fa-chevron-left"
            @click="previousMonth"
            class="calendar-nav-btn date-grid-nav-btn"
            :disabled="isPreviousMonthDisabled"
            :aria-label="monthNavLabels ? `Mois précédent : ${previousMonthName}` : 'Mois précédent'"
            data-testid="availability-calendar-previous"
            size="sm"
          >
            <template v-if="monthNavLabels">{{ previousMonthName }}</template>
          </Button>
          <h5 class="calendar-month-year">{{ currentMonthYear }}</h5>
          <Button
            type="button"
            variant="secondary"
            :icon="monthNavLabels ? null : 'fas fa-chevron-right'"
            @click="nextMonth"
            class="calendar-nav-btn date-grid-nav-btn"
            :aria-label="monthNavLabels ? `Mois suivant : ${nextMonthName}` : 'Mois suivant'"
            data-testid="availability-calendar-next"
            size="sm"
          >
            <template v-if="monthNavLabels">
              {{ nextMonthName }}
              <i class="fas fa-chevron-right" aria-hidden="true"></i>
            </template>
          </Button>
        </div>
        <div class="calendar-grid date-grid">
          <div class="calendar-weekday" v-for="day in weekDays" :key="day">{{ day }}</div>
          <!-- Chaque jour est un bouton : il se coche au clavier (Tab, puis
               Entrée ou Espace), dit la date entière et son état aux lecteurs
               d'écran, et se désactive pour un jour passé ou en lecture. -->
          <button
            v-for="(date, index) in calendarDays"
            :key="index"
            type="button"
            :disabled="isDateDisabled(date.date) || readonly"
            :aria-pressed="isDateSelected(date.date) ? 'true' : 'false'"
            :aria-label="formatDayLabel(date.date)"
            :class="[
              'calendar-day',
              'date-grid-cell',
              {
                'calendar-day-other-month': date.isOtherMonth,
                'calendar-day-selected': isDateSelected(date.date),
                'date-grid-cell-selected': isDateSelected(date.date),
                'calendar-day-disabled': isDateDisabled(date.date) || readonly,
                'date-grid-cell-disabled': isDateDisabled(date.date) || readonly,
                'calendar-day-readonly': readonly,
                'date-grid-cell-readonly': readonly
              }
            ]"
            @click="!readonly && toggleDate(date.date)"
          >
            {{ date.date.getDate() }}
          </button>
        </div>
      </div>
      <p v-if="!readonly" class="mode-hint">
        Cliquez sur les jours envisagés dans le calendrier : le devis affichera les dates précises retenues.
      </p>
      <div
        v-if="modelValue.length > 0"
        class="selected-dates date-grid-summary"
        :class="{ 'date-grid-summary--flat': readonly }"
      >
        <strong>{{ readonly ? 'Dates retenues' : 'Dates sélectionnées :' }}</strong>
        <div class="selected-dates-list date-grid-summary-list">
          <span
            v-for="(date, index) in sortedSelectedDates"
            :key="index"
            class="selected-date-tag date-grid-tag"
          >
            {{ formatDateForDisplay(date) }}
            <Button
              v-if="!readonly"
              type="button"
              variant="close"
              icon="fas fa-times"
              @click="removeDate(date)"
              class="remove-date-btn date-grid-tag-remove-btn"
              :aria-label="`Retirer la date ${formatDateForDisplay(date)}`"
              size="sm"
            />
          </span>
        </div>
      </div>
      <p v-else-if="!readonly" class="empty-state-hint">
        Aucune date sélectionnée pour l'instant.
      </p>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { toast } from 'vue-sonner'
import '../../styles/date-grid-picker.css'
import { useCalendar } from '../../composables/calendar/useCalendar.js'
import { Button, RadioGroup } from '../ui'
import MonthPeriodPicker from './MonthPeriodPicker.vue'

const props = defineProps({
  modelValue: {
    type: Array,
    default: () => []
  },
  datesInconnues: {
    type: Boolean,
    default: false
  },
  readonly: {
    type: Boolean,
    default: false
  },
  // Périodes approximatives (mois entiers "YYYY-MM"), exclusives des jours précis
  periods: {
    type: Array,
    default: () => []
  },
  // Affiche le sélecteur « jours précis / période approximative » (back-office uniquement)
  allowPeriods: {
    type: Boolean,
    default: false
  },
  // Flèches de mois nommées (« Août », « Octobre ») au lieu de l'icône nue.
  // Demandé par /calendar seul : /form et /embed/form gardent leurs flèches.
  monthNavLabels: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:modelValue', 'update:periods'])

const modeOptions = [
  { value: 'days', label: 'Dates précises' },
  { value: 'periods', label: 'Période approximative (mois)' }
]

const mode = ref(props.periods.length > 0 ? 'periods' : 'days')

// Le mode suit les données quand elles changent de l'extérieur (ouverture de
// modale, chargement de la demande) : des périodes enregistrées ⇒ mode période.
watch(() => props.periods, (value) => {
  if (value?.length) mode.value = 'periods'
}, { deep: true })

watch(() => props.modelValue, (value) => {
  if (value?.length && !props.periods?.length) mode.value = 'days'
}, { deep: true })

// Les deux saisies sont exclusives : basculer vide l'autre. On prévient
// l'utilisateur par un toast quand des données étaient déjà saisies dans
// l'autre mode, pour qu'il ne les perde pas sans s'en rendre compte.
const setMode = (value) => {
  if (value === mode.value) return
  mode.value = value
  if (value === 'periods') {
    if (props.modelValue?.length) {
      emit('update:modelValue', [])
      toast.info('Les dates précédemment saisies ont été effacées.')
    }
  } else {
    if (props.periods?.length) {
      emit('update:periods', [])
      toast.info('Les dates précédemment saisies ont été effacées.')
    }
  }
}

const showPeriods = computed(() => props.allowPeriods && mode.value === 'periods')

const selectedDates = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value)
})

const {
  currentMonth,
  currentYear,
  weekDays,
  currentMonthYear,
  calendarDays,
  formatDateForDisplay,
  isDateSelected,
  isDateDisabled,
  toggleDate: toggleDateCalendar,
  removeDate,
  previousMonth,
  nextMonth,
  isPreviousMonthDisabled,
  sortedSelectedDates
} = useCalendar(selectedDates)

const toggleDate = (date) => {
  if (!props.readonly) {
    toggleDateCalendar(date)
  }
}

/** « Août » : le mois où mène une flèche (le Date normalise -1 et 12). */
const monthName = (offset) => {
  const label = new Date(currentYear.value, currentMonth.value + offset, 1)
    .toLocaleDateString('fr-FR', { month: 'long' })
  return label.charAt(0).toUpperCase() + label.slice(1)
}

/** « mardi 22 septembre 2026 » : le nom lu d'une case, dont le texte n'est que « 22 ». */
const formatDayLabel = (date) =>
  date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })

const previousMonthName = computed(() => monthName(-1))
const nextMonthName = computed(() => monthName(1))
</script>

<style scoped>
/* Styles communs à la grille (jours/mois) partagés avec MonthPeriodPicker :
   voir src/styles/date-grid-picker.css. Seules les spécificités du calendrier
   par jour (mise en page de la cellule, colonnes, libellés de semaine, jours
   hors mois) restent ici. */

.calendar-container {
  margin: 1rem 0;
}

.mode-selector {
  margin-bottom: 0.75rem;
}

.mode-hint {
  margin: 0.5rem 0 0;
  font-size: 0.875rem;
  color: var(--color-text-secondary);
}

.empty-state-hint {
  margin: 0.75rem 0 0;
  font-size: 0.875rem;
  color: var(--color-text-tertiary);
  font-style: italic;
}

.calendar-month-year {
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--color-text-primary);
  text-transform: capitalize;
}

.calendar-grid {
  grid-template-columns: repeat(7, 1fr);
}

/* Flèches nommées (`month-nav-labels`, /calendar seul) : un mot et une flèche
   dans une cible de 44 px. La règle de date-grid-picker.css porte la flèche nue
   à 1,5 rem, un mot à cette taille déborderait de l'en-tête. */
.calendar-month-labels .date-grid-nav-btn {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-2);
  min-height: 44px;
  padding: 0 var(--spacing-3);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--color-text-primary);
}

.calendar-weekday {
  background: var(--color-bg-secondary);
  padding: var(--spacing-sm);
  text-align: center;
  font-weight: 600;
  font-size: 0.875rem;
  color: var(--color-text-secondary);
}

.calendar-day {
  padding: var(--spacing-sm);
  min-height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.calendar-day-other-month {
  opacity: 0.4;
  background: var(--color-bg-secondary);
}

@media (max-width: 768px) {
  .calendar-day {
    min-height: 2rem;
  }

  .calendar-weekday {
    font-size: 0.75rem;
    padding: 0.5rem 0.25rem;
  }
}
</style>

